/**
 * 数据权限管理 API
 * 对应 foundation-service (7092)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type {
  DataScopeRule,
  FieldPermissionRule,
  DataPermissionQueryParams,
  FieldPermissionQueryParams,
  DataScopeFormData,
  FieldPermissionFormData,
  ResourceTypeOption,
  DataPermissionStatistics,
} from './types';

const service = () => unifiedApi.service('foundation');
const BASE_URL = '/data-permissions';

// ==================== 数据范围 API ====================

/** 获取数据范围列表 */
export const getDataScopeList = async (params?: DataPermissionQueryParams) => {
  return handleApiResponse(
    service().get<{ list: DataScopeRule[]; total: number }>(`${BASE_URL}/scopes`, { params })
  );
};

/** 获取数据范围详情 */
export const getDataScopeDetail = async (id: number) => {
  return handleApiResponse(
    service().get<DataScopeRule>(`${BASE_URL}/scopes/${id}`)
  );
};

/** 创建数据范围 */
export const createDataScope = async (data: DataScopeFormData) => {
  return handleApiResponse(
    service().post<{ id: number }>(`${BASE_URL}/scopes`, data)
  );
};

/** 批量创建数据范围 */
export const batchCreateDataScopes = async (data: DataScopeFormData[]) => {
  return handleApiResponse(
    service().post(`${BASE_URL}/scopes/batch`, { scopes: data })
  );
};

/** 更新数据范围 */
export const updateDataScope = async (id: number, data: DataScopeFormData) => {
  return handleApiResponse(
    service().put(`${BASE_URL}/scopes/${id}`, data)
  );
};

/** 删除数据范围 */
export const deleteDataScope = async (id: number) => {
  return handleApiResponse(
    service().delete(`${BASE_URL}/scopes/${id}`)
  );
};

/** 批量删除数据范围 */
export const batchDeleteDataScopes = async (ids: number[]) => {
  return handleApiResponse(
    service().post(`${BASE_URL}/scopes/batch-delete`, { ids })
  );
};

// ==================== 字段权限 API ====================

/** 获取字段权限列表 */
export const getFieldPermissionList = async (params?: FieldPermissionQueryParams) => {
  return handleApiResponse(
    service().get<{ list: FieldPermissionRule[]; total: number }>(`${BASE_URL}/fields`, { params })
  );
};

/** 获取字段权限详情 */
export const getFieldPermissionDetail = async (id: number) => {
  return handleApiResponse(
    service().get<FieldPermissionRule>(`${BASE_URL}/fields/${id}`)
  );
};

/** 创建字段权限 */
export const createFieldPermission = async (data: FieldPermissionFormData) => {
  return handleApiResponse(
    service().post<{ id: number }>(`${BASE_URL}/fields`, data)
  );
};

/** 批量创建字段权限 */
export const batchCreateFieldPermissions = async (data: FieldPermissionFormData[]) => {
  return handleApiResponse(
    service().post(`${BASE_URL}/fields/batch`, { permissions: data })
  );
};

/** 更新字段权限 */
export const updateFieldPermission = async (id: number, data: FieldPermissionFormData) => {
  return handleApiResponse(
    service().put(`${BASE_URL}/fields/${id}`, data)
  );
};

/** 删除字段权限 */
export const deleteFieldPermission = async (id: number) => {
  return handleApiResponse(
    service().delete(`${BASE_URL}/fields/${id}`)
  );
};

/** 批量删除字段权限 */
export const batchDeleteFieldPermissions = async (ids: number[]) => {
  return handleApiResponse(
    service().post(`${BASE_URL}/fields/batch-delete`, { ids })
  );
};

// ==================== 角色数据权限 API ====================

/** 获取角色数据权限 */
export const getRoleDataPermissions = async (roleId: number) => {
  return handleApiResponse(
    service().get<{ scopes: DataScopeRule[]; fields: FieldPermissionRule[] }>(`${BASE_URL}/roles/${roleId}`)
  );
};

/** 保存角色数据权限 */
export const saveRoleDataPermissions = async (data: { role_id: number; scopes: DataScopeFormData[]; fields: FieldPermissionFormData[] }) => {
  return handleApiResponse(
    service().post(`${BASE_URL}/roles`, data)
  );
};

// ==================== 用户数据权限 API ====================

/** 获取用户数据权限 */
export const getUserDataPermissions = async (userId: number) => {
  return handleApiResponse(
    service().get<{ scopes: DataScopeRule[]; fields: FieldPermissionRule[] }>(`${BASE_URL}/users/${userId}`)
  );
};

// ==================== 权限检查 API ====================

/** 检查数据权限 */
export const checkDataPermission = async (data: { resource_type: string; resource_id?: string }) => {
  return handleApiResponse(
    service().post<{ allowed: boolean; scope_type: string }>(`${BASE_URL}/check/data`, data)
  );
};

/** 检查字段权限 */
export const checkFieldPermission = async (data: { resource_type: string; field_name: string }) => {
  return handleApiResponse(
    service().post<{ permission: string; mask_type?: string }>(`${BASE_URL}/check/field`, data)
  );
};

// ==================== 选项和配置 API ====================

/** 获取数据权限选项 */
export const getDataPermissionOptions = async () => {
  return handleApiResponse(
    service().get<{
      scope_types: Array<{ value: string; label: string }>;
      permission_types: Array<{ value: string; label: string }>;
      mask_types: Array<{ value: string; label: string }>;
    }>(`${BASE_URL}/options`)
  );
};

/** 获取资源类型列表 */
export const getResourceTypes = async () => {
  return handleApiResponse(
    service().get<{ types: ResourceTypeOption[] }>(`${BASE_URL}/resource-types`)
  );
};

/** 从模板同步权限 */
export const syncFromTemplate = async (data: { role_id: number; template_id: number }) => {
  return handleApiResponse(
    service().post(`${BASE_URL}/sync-from-template`, data)
  );
};

/** 获取数据权限统计 */
export const getDataPermissionStatistics = async () => {
  return handleApiResponse(
    service().get<DataPermissionStatistics>(`${BASE_URL}/statistics`)
  );
};

/** 数据权限管理 API 对象 */
export const dataPermissionApi = {
  // 数据范围
  getScopeList: getDataScopeList,
  getScopeDetail: getDataScopeDetail,
  createScope: createDataScope,
  batchCreateScopes: batchCreateDataScopes,
  updateScope: updateDataScope,
  deleteScope: deleteDataScope,
  batchDeleteScopes: batchDeleteDataScopes,
  // 字段权限
  getFieldList: getFieldPermissionList,
  getFieldDetail: getFieldPermissionDetail,
  createField: createFieldPermission,
  batchCreateFields: batchCreateFieldPermissions,
  updateField: updateFieldPermission,
  deleteField: deleteFieldPermission,
  batchDeleteFields: batchDeleteFieldPermissions,
  // 角色权限
  getRolePermissions: getRoleDataPermissions,
  saveRolePermissions: saveRoleDataPermissions,
  // 用户权限
  getUserPermissions: getUserDataPermissions,
  // 权限检查
  checkData: checkDataPermission,
  checkField: checkFieldPermission,
  // 选项和配置
  getOptions: getDataPermissionOptions,
  getResourceTypes,
  syncFromTemplate,
  getStatistics: getDataPermissionStatistics,
};

export default dataPermissionApi;
